
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Enrollment } from "@/types/classTypes";
import { supabase } from "@/lib/supabase";
import { AlertCircle, CheckCircle2, UserPlus } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface WaitlistManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  waitlistedEnrollments: Enrollment[];
  classId: string;
  organizationId: string;
  maxClassSize: number;
  currentEnrolled: number;
  onEnrollmentUpdated: () => void;
}

const WaitlistManagementDialog: React.FC<WaitlistManagementDialogProps> = ({
  open,
  onOpenChange,
  waitlistedEnrollments,
  classId,
  organizationId,
  maxClassSize,
  currentEnrolled,
  onEnrollmentUpdated
}) => {
  const [processing, setProcessing] = useState(false);
  const [selectedEnrollments, setSelectedEnrollments] = useState<string[]>([]);

  const availableSpots = Math.max(0, maxClassSize - currentEnrolled);
  
  const toggleEnrollmentSelection = (enrollmentId: string) => {
    if (selectedEnrollments.includes(enrollmentId)) {
      setSelectedEnrollments(selectedEnrollments.filter(id => id !== enrollmentId));
    } else {
      // Only allow selecting up to the number of available spots
      if (selectedEnrollments.length < availableSpots) {
        setSelectedEnrollments([...selectedEnrollments, enrollmentId]);
      } else {
        toast.error(`Cannot select more than ${availableSpots} ${availableSpots === 1 ? 'student' : 'students'}`);
      }
    }
  };
  
  const activateEnrollments = async () => {
    if (selectedEnrollments.length === 0) {
      toast.error("Please select at least one student to move from the waitlist");
      return;
    }
    
    if (selectedEnrollments.length > availableSpots) {
      toast.error(`You can only move ${availableSpots} ${availableSpots === 1 ? 'student' : 'students'} from the waitlist`);
      return;
    }
    
    setProcessing(true);
    
    try {
      const { error } = await supabase
        .from('enrollments')
        .update({ status: 'active' })
        .in('id', selectedEnrollments);
      
      if (error) throw error;
      
      toast.success(`Successfully moved ${selectedEnrollments.length} ${selectedEnrollments.length === 1 ? 'student' : 'students'} from the waitlist`);
      
      // Trigger refresh of enrollments
      onEnrollmentUpdated();
      
      // Close the dialog
      onOpenChange(false);
    } catch (error) {
      console.error("Error updating waitlist enrollments:", error);
      toast.error("Failed to update waitlist enrollments");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Waitlist Management</DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          {availableSpots > 0 ? (
            <div className="bg-green-50 text-green-700 px-4 py-3 rounded-md mb-4 flex items-start">
              <CheckCircle2 className="h-5 w-5 mt-0.5 mr-2 flex-shrink-0" />
              <div>
                <p className="font-medium">Available Spots: {availableSpots}</p>
                <p className="text-sm">Select up to {availableSpots} {availableSpots === 1 ? 'student' : 'students'} to move from the waitlist.</p>
              </div>
            </div>
          ) : (
            <div className="bg-amber-50 text-amber-700 px-4 py-3 rounded-md mb-4 flex items-start">
              <AlertCircle className="h-5 w-5 mt-0.5 mr-2 flex-shrink-0" />
              <div>
                <p className="font-medium">No Available Spots</p>
                <p className="text-sm">The class is currently at full capacity. You'll need to increase the class size or remove existing students before adding from the waitlist.</p>
              </div>
            </div>
          )}
          
          <div className="space-y-1">
            <h3 className="text-sm font-medium">Students on Waitlist</h3>
            
            {waitlistedEnrollments.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">No students on the waitlist.</p>
            ) : (
              <div className="space-y-2 mt-2 max-h-[300px] overflow-y-auto pr-2">
                {waitlistedEnrollments.map((enrollment) => (
                  <div 
                    key={enrollment.id}
                    className={`flex items-center justify-between p-3 rounded-md border ${
                      selectedEnrollments.includes(enrollment.id) 
                        ? 'bg-primary/10 border-primary' 
                        : 'bg-card hover:bg-accent/20'
                    } cursor-pointer transition-colors`}
                    onClick={() => toggleEnrollmentSelection(enrollment.id)}
                  >
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                        <UserPlus className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">
                          {enrollment.student?.first_name} {enrollment.student?.last_name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Waitlisted: {new Date(enrollment.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <Badge variant={selectedEnrollments.includes(enrollment.id) ? "default" : "outline"}>
                      {selectedEnrollments.includes(enrollment.id) ? "Selected" : "Select"}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={activateEnrollments} 
            disabled={processing || selectedEnrollments.length === 0 || availableSpots === 0}
          >
            {processing ? "Processing..." : "Move to Active"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WaitlistManagementDialog;
